// The Customers tab's filter state, in the two shapes it has to exist in: the
// browser URL and the query string sent to GET /api/customers/list.
//
// The twin of web/src/components/tickets/queue-query.js, and it exists for the same
// two reasons. First, the URL and the API call are close but not identical — the URL
// omits defaults so a clean list has a clean address, while the API call is explicit
// about limit — and writing that twice in a page component is how they drift.
// Second, and the reason this file is shaped the way it is: there is NO DOM test
// harness in this repository. `test/web/queue-query.test.js` can be a plain
// `node --test` file only because its subject is pure functions over
// URLSearchParams — no React, no DOM, no build step. So every decision here that
// could be silently wrong is pushed OUT of the components and into this module,
// where test/web/customer-query.test.js can reach it.
//
// Only documented params are ever emitted. An unrecognised one is
// 400 INVALID_QUERY_PARAM on purpose (docs/API.md §1.6): a typo that quietly widens
// a directory search is worse than a failed request.

export const PAGE_SIZE = 25;
export const DEFAULT_SORT = 'name';

/**
 * URLSearchParams → the shape the filter bar, the table and both serialisers read.
 *
 * `offset` is parsed defensively. A hand-edited or truncated URL (`?offset=`,
 * `?offset=abc`, `?offset=-5`) must read back as 0, never as NaN: `NaN` would reach
 * the API as the string "NaN", fail validation, and turn a mangled link into an
 * error card instead of page one.
 */
export function readFilters(searchParams) {
  const offset = Number.parseInt(searchParams.get('offset') ?? '0', 10);
  return {
    q: searchParams.get('q') ?? '',
    organizationId: searchParams.get('organization_id') ?? '',
    // Display only — see toApiQuery. This is what lets a shared link show
    // "Bluebird Cafe" in the organization picker without a round trip to resolve
    // the id, and it is why the picker can be a typeahead over an endpoint that
    // has no list-all mode.
    organizationName: searchParams.get('organization_name') ?? '',
    locationId: searchParams.get('location_id') ?? '',
    sort: searchParams.get('sort') ?? DEFAULT_SORT,
    offset: Number.isFinite(offset) && offset > 0 ? offset : 0,
  };
}

/** What goes in the address bar: shareable, and free of redundant defaults. */
export function toSearchParams(filters) {
  const params = new URLSearchParams();
  const q = filters.q.trim();
  // Kept in the URL even when it is one character, unlike toApiQuery below. The box
  // is a controlled input synced from the URL, so dropping a half-typed term here
  // would erase it from under the agent on the next render.
  if (q) params.set('q', q);
  if (filters.organizationId) {
    params.set('organization_id', filters.organizationId);
    // Only ever beside the id. A name with no id would filter nothing and label the
    // picker with a scope that is not applied.
    if (filters.organizationName) params.set('organization_name', filters.organizationName);
  }
  if (filters.locationId) params.set('location_id', filters.locationId);
  // Omitted when it equals the default, so an unsorted list has a clean address.
  // This is the OPPOSITE of the queue's rule, and deliberately so: the queue has
  // saved views whose own ordering an explicit default has to be able to override,
  // so there `sort === DEFAULT_SORT` is a meaningful choice. There are no customer
  // views (design §6), so here it is redundant.
  if (filters.sort && filters.sort !== DEFAULT_SORT) params.set('sort', filters.sort);
  if (filters.offset) params.set('offset', String(filters.offset));
  // Never `limit`: PAGE_SIZE is a property of this build, not of the link.
  return params;
}

/** What goes to GET /api/customers/list. */
export function toApiQuery(filters) {
  const params = new URLSearchParams();
  // A `q` shorter than two characters is 400 INVALID_QUERY_PARAM, so it never
  // leaves the browser; the filter bar's hint line says why instead of an error
  // card doing it. Dropping it here rather than refusing to search means a
  // half-typed term shows the unfiltered list, which is the honest answer.
  const q = filters.q.trim();
  if (q.length >= 2) params.set('q', q);
  if (filters.organizationId) params.set('organization_id', filters.organizationId);
  if (filters.locationId) params.set('location_id', filters.locationId);
  // `organization_name` is display-only and is NOT sent: it is not a documented
  // param, so sending it would be 400 INVALID_QUERY_PARAM for every filtered
  // request — the whole page, not just the picker's label.
  if (filters.sort && filters.sort !== DEFAULT_SORT) params.set('sort', filters.sort);
  // Always explicit, unlike the URL: the server's own default is 25 today, and a
  // page size the client renders against must not be something the server can
  // change underneath it.
  params.set('limit', String(PAGE_SIZE));
  if (filters.offset) params.set('offset', String(filters.offset));
  return params.toString();
}

/**
 * How one customer is named on screen, in one place.
 *
 * This is migration 018's null-email rule, made concrete. `customers.email` is
 * nullable and `customers_email_idx` is filtered on `email IS NOT NULL`, so a row
 * with no address at all is legal and ordinary — the cashier or driver who rings
 * the desk. Every display path therefore has to survive `{ name: null, email: null }`
 * without rendering "undefined", and no path may use `email` as a React key.
 *
 * `secondary` prefers the ORGANIZATION name over the free-text `company` column:
 * the organization is a real relation that the desk can act on, while `company` is
 * whatever a Zendesk import or an inbound mail signature happened to say. That is
 * the same precedence CallerPanel and RequesterList already use.
 */
export function customerIdentity(customer) {
  const primary = customer?.name || customer?.email || '(no name)';
  const secondary = [
    customer?.organization?.name || customer?.company || '',
    customer?.email || '',
  ]
    .filter(Boolean)
    .join(' · ');
  return { primary, secondary };
}

/**
 * Whether anything is filtering the list — what decides if "Clear filters" is
 * offered. Sort is excluded on purpose: an ordering is not a filter, and clearing
 * filters must not silently reorder the table under the agent.
 */
export function hasActiveFilters(filters) {
  return filters.q.trim() !== '' || filters.organizationId !== '' || filters.locationId !== '';
}

/**
 * The empty-state sentence, which has to NAME what was searched. "No results" that
 * does not say what it searched for is a dead end — the agent cannot tell a typo
 * from a genuinely unknown caller.
 */
export function emptyMessage(filters) {
  const q = filters.q.trim();
  if (q) return `No contacts match “${q}”.`;
  if (filters.organizationName) return `No contacts at ${filters.organizationName}.`;
  if (filters.organizationId || filters.locationId) return 'No contacts match these filters.';
  return 'No contacts yet.';
}
